import { RasterLayerSpecification, SymbolLayerSpecification } from 'mapbox-gl';
import type { MapRef } from 'react-map-gl/mapbox';
import { COLOR_NEUTRAL_WHITE, FONT_SIZE_100 } from '@alltrails/denali/tokens';
import { parkPinVerticalOffset, pinMarkerVerticalOffset, poiPinVerticalOffset } from './svgDefinitions/svgMarkerComponents';

export const getChevronsId = (uniqueId?: string) => (uniqueId ? `chevrons-${uniqueId}` : 'chevrons');
export const getPolylineId = (uniqueId?: string) => (uniqueId ? `polyline-${uniqueId}` : 'polyline');
export const getOutlineId = (uniqueId?: string) => (uniqueId ? `outline-${uniqueId}` : 'outline');

export const getHeatmapPointsId = (uniqueId?: string) => (uniqueId ? `heatmap-points-${uniqueId}` : 'heatmap-points');
export const getHeatmapPolylinesId = (uniqueId?: string) => (uniqueId ? `heatmap-polylines-${uniqueId}` : 'heatmap-polylines');

export const getPolygonFillId = (uniqueId: string | number) => `polygon-fill-${uniqueId}`;
export const getPolygonOutlineId = (uniqueId: string | number) => `polygon-outline-${uniqueId}`;

export const getClusteredPinsId = (uniqueId: string) => `clustered-pins-${uniqueId}`;
export const getUnclusteredPinsId = (uniqueId: string) => `unclustered-pins-${uniqueId}`;

export const getLargePOIPinsId = (uniqueId: string) => `poi-large-${uniqueId}`;

export const customRouteActiveSegmentUniqueId = 'custom-route-active-segment';

export const atmosphereId = 'atmosphere';
export const chevronsBottomId = 'chevrons-bottom';
export const chevronsTopId = 'chevrons-top';
export const communityHeatmapId = 'community-heatmap-raster';
export const publicLandsId = 'public-lands';
export const conditionsOverlaysBottomId = 'conditions-overlays-bottom';
export const conditionsOverlaysTopId = 'conditions-overlays-top';
export const curatedWaypointsId = 'curated-waypoints';
export const currentMarkerId = 'current-marker';
export const customRouteActiveSegmentPolylineId = getPolylineId(customRouteActiveSegmentUniqueId);
export const customRouteActiveSegmentChevronsId = getChevronsId(customRouteActiveSegmentUniqueId);
export const customRouteMarkersId = 'custom-route-markers';
export const customRouteMarkerDragId = 'custom-route-marker-drag';
export const customRouteWaypointsId = 'custom-route-waypoints';
export const distanceAwayCenterMarkerId = 'distance-away-center';
export const distanceMarkersId = 'distance-markers';
export const endMarkerId = 'end-marker';
export const geoBoundariesOverlayId = 'geo-boundaries-overlay';
export const geoBoundariesOverlayOutlineId = 'geo-boundaries-overlay-outline';
export const isochroneFillId = 'isochrone-fill';
export const isochroneOutlineId = 'isochrone-outline';
export const osmLinesId = 'osm-alltrails-lines-public';
export const parkPinsId = 'park-pins';
// Split park pins for z-ordering in unclustering experiment
export const nationalParkPinsId = 'national-park-pins';
export const stateParkPinsId = 'state-park-pins';
export const poiPopupPinId = 'poi-popup-pin';
export const poiLargeId = 'poi-large';
export const poiPinsId = 'poi-pins';
export const polylinesBottomId = 'polylines-bottom';
export const polylinesTopId = 'polylines-top';
export const startEndMarkerId = 'start-end-marker';
export const startMarkerId = 'start-marker';
export const tapToRemoveMarkerId = 'tap-to-remove';
export const trailCollectionLinesId = 'trail-collection-lines';
export const trailClustersId = 'trail-clusters';
export const trailPinsId = 'trail-pins';
// Split trail pins for z-ordering in unclustering experiment
export const regularTrailPinsId = 'regular-trail-pins';
export const collapsedTrailPinsId = 'collapsed-trail-pins';
export const collapsedTrailPinsActiveId = 'collapsed-trail-pins-active';
export const userCustomMapPinId = 'user-custom-map-pins';
export const userCustomMapPinClusterId = 'user-custom-map-pin-cluster';
export const userPhotosId = 'user-photos';
export const userWaypointsId = 'user-waypoints';
export const photospotId = 'photospots';
export const hoveredPolylineId = 'hovered-polyline';
// Community activity layer
export const communityActivityClusterPinsId = 'community-activity-cluster-pins';

// These layers will render under featureLayers and will be ignored in event handlers
const fullscreenLayers = [publicLandsId, conditionsOverlaysBottomId, conditionsOverlaysTopId, communityHeatmapId];

// These layers should render in order, with the latter layers rendering overtop of earlier layers
// Keeping this order ensures that the order of layers is maintained, even when a user switches base map styles (which causes the layers to re-render)
export const featureLayers = [
  // We put these ids on empty layers so that we can group all polylines, which have unique layer ids,
  // overtop of map overlays but underneath map markers
  polylinesBottomId,
  customRouteActiveSegmentPolylineId,
  hoveredPolylineId,
  polylinesTopId,
  osmLinesId,
  chevronsBottomId,
  chevronsTopId,
  isochroneFillId,
  isochroneOutlineId,
  communityActivityClusterPinsId,
  trailClustersId,
  trailPinsId,
  // Unclustering experiment layers - ordered bottom to top:
  // collapsed trails (inactive, then active) → POI pins → state parks → regular trails → national parks
  collapsedTrailPinsId,
  poiPinsId,
  poiPopupPinId,
  poiLargeId,
  stateParkPinsId,
  regularTrailPinsId,
  nationalParkPinsId,
  // Show active collapsed trail pins on top of other layers
  collapsedTrailPinsActiveId,
  // Legacy park pins layer (used when experiment is off)
  parkPinsId,
  tapToRemoveMarkerId,
  distanceAwayCenterMarkerId,
  userWaypointsId,
  userPhotosId,
  startMarkerId,
  endMarkerId,
  startEndMarkerId,
  distanceMarkersId,
  curatedWaypointsId,
  currentMarkerId,
  photospotId,
  customRouteWaypointsId,
  customRouteMarkersId,
  customRouteMarkerDragId,
  userCustomMapPinClusterId,
  userCustomMapPinId
];

export const additionalMapLayers = [...fullscreenLayers, ...featureLayers];
export const getLayerBeforeId = (map: MapRef | undefined, layerId: string) => {
  if (!map) {
    return undefined;
  }
  // If the layerId isn't found, this returns -1, which is OK because we then search through the whole array.
  // This means that any unspecified layer will render below all of these layers.
  // If other behavior is needed, the layer needs to be specified in the array and logic may need to be added
  // to this function since some layers have dynamic ids (may need to search for strings containing 'heatmap-', for example)
  const index = additionalMapLayers.indexOf(layerId);

  // Copy of the array needed so that splice doesn't alter the array
  return [...additionalMapLayers].splice(index + 1).find(layer => Boolean(map.getLayer(layer)));
};

// These properties must be enabled to make sure an icon is not hidden if it
// collides with another icon/symbol. Note that these properties are considered
// "arcane" and will be done away with at some point. This issue is still open
// at time of writing. https://github.com/mapbox/mapbox-gl-js/issues/4117
// See examples here https://docs.mapbox.com/help/troubleshooting/optimize-map-label-placement/
const stylesThatAllowCollisions: SymbolLayerSpecification['layout'] = {
  'icon-allow-overlap': false,
  'icon-ignore-placement': true
};

export const defaultSymbolLayerLayoutProps: SymbolLayerSpecification['layout'] = {
  ...stylesThatAllowCollisions,
  'text-font': ['Manrope Bold'],
  'text-size': FONT_SIZE_100
};

export const defaultPinLayerLayoutProps: SymbolLayerSpecification['layout'] = {
  'icon-anchor': 'bottom',
  'icon-offset': [0, pinMarkerVerticalOffset] // Move the pin down slightly so the bottom of the pin hits the correct coordinates
};

export const defaultParkPinLayerLayoutProps: SymbolLayerSpecification['layout'] = {
  'icon-anchor': 'bottom',
  'icon-offset': [0, parkPinVerticalOffset]
};

export const defaultPOIPinLayerLayoutProps: SymbolLayerSpecification['layout'] = {
  'icon-anchor': 'bottom',
  'icon-offset': [0, poiPinVerticalOffset],
  'text-anchor': 'top',
  'text-font': ['Manrope Medium', 'Arial Unicode MS Regular']
};

export const defaultPOIPinLayerPaintProps: SymbolLayerSpecification['paint'] = {
  'text-halo-color': COLOR_NEUTRAL_WHITE,
  'text-halo-width': 1.5
};

// This is still a WIP with data science and design
export const communityHeatmapLayerPaintProps: RasterLayerSpecification['paint'] = {
  'raster-color-range': [0, 1],
  'raster-color': [
    'interpolate-lab',
    ['exponential', 1],
    ['raster-value'],
    0,
    'rgba(231, 83, 253, 0.2)',
    0.25,
    'rgba(231, 83, 253, 0.3)',
    0.5,
    'rgba(231, 83, 253, 0.6)',
    0.65,
    'rgba(162, 54, 247, 0.7)',
    0.95,
    'rgba(93, 25, 240, 0.8)'
  ],
  'raster-hue-rotate': 0
};

// Lighter purple, for use when the base map is 'alltrailsSatellite' or 'googleSatellite':
export const communityHeatmapLayerWithSatellitePaintProps: RasterLayerSpecification['paint'] = {
  'raster-color-range': [0, 1],
  'raster-color': [
    'interpolate-lab',
    ['exponential', 1],
    ['raster-value'],
    0,
    'rgba(88, 1, 101, 0.2)',
    0.25,
    'rgba(88, 1, 101, 0.3)',
    0.5,
    'rgba(136, 2, 176, 0.6)',
    0.65,
    'rgba(183, 120, 252, 0.7)',
    0.95,
    'rgba(255, 255, 255, 0.8)'
  ],
  'raster-hue-rotate': 0
};
